import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import FeaturedAthletesCarousel from "@/components/FeaturedAthletesCarousel";
import { formatTournamentDate } from "@/components/TournamentDetailsDialog";
import TournamentPreviewCard from "@/components/TournamentPreviewCard";
import AnimatedNumber from "@/components/AnimatedNumber";
import TiltCard from "@/components/TiltCard";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import {
  ArrowDown,
  ArrowRight,
  GraduationCap,
  Trophy,
  TrendingUp,
} from "lucide-react";

const PERFORMANCE_PILLARS = [
  {
    icon: GraduationCap,
    title: "Treinamento Individual",
    description:
      "Plano mensal personalizado por categoria, evolução e nível competitivo do atleta.",
  },
  {
    icon: TrendingUp,
    title: "Acompanhamento Real",
    description:
      "Feedback semanal, frequência, metas e histórico esportivo em um único painel.",
  },
  {
    icon: Trophy,
    title: "Competição de Elite",
    description:
      "Preparação técnica e mental para torneios estaduais, nacionais e FIDE.",
  },
];

const PERFORMANCE_STATS = [
  { target: 1000, prefix: "+", suffix: "", label: "Alunos formados" },
  { target: 13, prefix: "", suffix: "", label: "Anos de tradição" },
  { target: 4, prefix: "", suffix: "x", label: "JEBs consecutivos" },
];

// Set to false to remove only the scroll accent from the overlapping section.
const ENABLE_HOME_OVERLAP_EFFECT = true;

export default function Home() {
  const [tournaments, setTournaments] = useState([]);
  const [tournamentStatus, setTournamentStatus] = useState("loading");
  const overlapPanelRef = useRef(null);

  useEffect(() => {
    if (!ENABLE_HOME_OVERLAP_EFFECT || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }

    const panel = overlapPanelRef.current;
    if (!panel) return undefined;

    let animationFrame = 0;
    const updateOverlap = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(() => {
        const progress = Math.min(Math.max(window.scrollY / 360, 0), 1);
        panel.style.setProperty("--home-overlap-progress", progress.toFixed(3));
        animationFrame = 0;
      });
    };

    updateOverlap();
    window.addEventListener("scroll", updateOverlap, { passive: true });
    return () => {
      window.removeEventListener("scroll", updateOverlap);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  useEffect(() => {
    api
      .get("/public/tournaments")
      .then((response) => {
        const today = new Date().toLocaleDateString("sv-SE");
        setTournaments(
          response.data
            .filter((tournament) => tournament.date >= today)
            .slice(0, 3),
        );
        setTournamentStatus("ready");
      })
      .catch(() => setTournamentStatus("error"));
  }, []);

  const featuredTournament = tournaments[0];

  const scrollToAbout = (event) => {
    const section = document.getElementById("sobre");
    if (!section) return;

    event.preventDefault();
    section.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "start",
    });
  };

  return (
    <PublicLayout>
      <section className="sticky top-0 isolate -mt-[68px] min-h-[690px] overflow-hidden border-b border-amber-500/10 pt-[68px] lg:min-h-[760px]">
        <img
          src="/brand/gsx-hero.jpeg"
          alt="Técnico do Grupo Santo de Xadrez concentrado em uma partida"
          className="absolute inset-0 -z-20 h-full w-full scale-[1.03] object-cover object-[63%_20%] blur-[3px]"
        />
        <div className="absolute inset-0 -z-10 bg-[#020b1d]/75" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#02091c]/92 via-[#061426]/73 to-[#061426]/38" />
        <div className="absolute inset-x-0 bottom-0 -z-10 h-2/5 bg-gradient-to-t from-[#061426] to-transparent" />

        <div className="relative mx-auto flex min-h-[622px] max-w-7xl flex-col justify-center px-4 pb-14 pt-12 sm:px-6 lg:min-h-[692px] lg:px-8 lg:pb-16">
          <div className="max-w-3xl lg:max-w-[610px]">
            <span className="inline-flex rounded-md border border-amber-400/35 bg-[#071a33]/85 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-300">
              Elite Chess Performance
            </span>

            <h1 className="mt-5 max-w-2xl text-4xl font-bold leading-[1.06] text-white sm:text-5xl lg:text-7xl">
              Onde talento se{" "}
              <span className="text-amber-400">transforma em resultado.</span>
            </h1>

            <p className="mt-6 max-w-xl text-sm font-medium leading-relaxed text-slate-200 sm:text-base">
              Uma plataforma premium de treinamento, acompanhamento e
              desenvolvimento esportivo para atletas do Grupo Santo de Xadrez.
              Planos individualizados, evolução mensurável e competição de verdade.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/aula-experimental">
                <Button
                  data-testid="hero-experimental-cta"
                  className="h-11 bg-amber-500 px-5 font-bold text-[#071a33] hover:bg-amber-400"
                >
                  Agendar Aula Experimental <ArrowRight className="ml-2" />
                </Button>
              </Link>

              <Link to="/torneios">
                <Button
                  variant="outline"
                  data-testid="hero-tournaments-cta"
                  className="h-11 border-amber-400/40 bg-[#071a33]/50 px-5 font-semibold text-amber-300 hover:bg-amber-500/10 hover:text-amber-200"
                >
                  Ver Torneios
                </Button>
              </Link>
            </div>
          </div>

          <div className="group mt-10 grid w-full max-w-[680px] self-end grid-cols-3 rounded-[22px] border border-white/15 bg-[#071426]/80 px-3 py-7 shadow-[0_18px_50px_rgba(0,0,0,0.28)] backdrop-blur-xl transition-[transform,background-color,border-color,box-shadow] duration-300 motion-safe:hover:-translate-y-1 hover:border-amber-400/45 hover:bg-[#0b213b]/90 hover:shadow-[0_22px_55px_rgba(201,162,39,0.18)] motion-reduce:transition-none sm:px-6 xl:absolute xl:right-8 xl:top-[29%] xl:mt-0 xl:min-h-[160px] xl:max-w-[580px] xl:items-center xl:py-8">
            {PERFORMANCE_STATS.map((stat, index) => (
              <div
                key={stat.label}
                className="relative rounded-lg px-3 py-2 transition-colors duration-300 hover:bg-amber-400/10 sm:px-5"
              >
                {index > 0 && (
                  <span
                    aria-hidden="true"
                    className="absolute bottom-2 left-0 top-2 w-px bg-gradient-to-b from-transparent via-amber-400/55 to-transparent"
                  />
                )}
                <p className="font-mono text-xl font-bold tracking-tight text-white transition-colors duration-300 group-hover:text-amber-50 sm:text-3xl">
                  <AnimatedNumber
                    value={stat.target}
                    prefix={stat.prefix}
                    suffix={stat.suffix}
                  />
                </p>
                <p className="mt-1.5 text-[10px] leading-tight text-slate-300 sm:text-[13px]">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        <a
          href="#sobre"
          onClick={scrollToAbout}
          aria-label="Role para conhecer o GSX"
          className="hero-scroll-cue absolute bottom-20 left-1/2 flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full border border-white/20 bg-[#071426]/65 text-white shadow-lg shadow-black/20 backdrop-blur-sm transition-colors hover:border-amber-400/50 hover:bg-amber-500 hover:text-[#071a33] sm:bottom-24"
        >
          <ArrowDown className="h-5 w-5" />
        </a>
      </section>

      <div
        ref={overlapPanelRef}
        className={`relative z-10 -mt-14 overflow-visible rounded-t-[2rem] border-t border-amber-400/15 bg-[#071A33] shadow-[0_-24px_64px_rgba(2,11,29,0.35)] sm:-mt-16 sm:rounded-t-[2.5rem]${ENABLE_HOME_OVERLAP_EFFECT ? " home-overlap-panel" : ""}`}
      >
        <section
          aria-label="Próximo evento"
          className="px-4 py-6 sm:px-6 sm:py-8 lg:px-8"
        >
          <div className="mx-auto max-w-7xl">
            <div className="flex min-h-[82px] flex-col items-start justify-center gap-3 sm:flex-row sm:items-center sm:gap-4">
              <span className="shrink-0 rounded-md border border-amber-300/25 bg-amber-400 px-3 py-1.5 text-[9px] font-extrabold uppercase tracking-wider text-[#071a33] shadow-sm shadow-amber-950/20">
                Próximos eventos
              </span>

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold leading-snug text-white sm:text-[15px]">
                  {featuredTournament
                    ? featuredTournament.name
                    : "17 e 18 de Outubro • Regional Nordeste + Sergipano"}
                </p>
                <p className="mt-1 truncate text-xs leading-relaxed text-slate-300">
                  {featuredTournament
                    ? `${formatTournamentDate(featuredTournament.date)} • ${featuredTournament.city || "GSX em campo"}`
                    : "Regional Nordeste Escolar, Sergipano Blitz e Regional Nordeste Rápido — GSX em campo"}
                </p>
              </div>

              <Link to="/torneios" className="shrink-0">
                <Button
                  variant="outline"
                  data-testid="hero-event-cta"
                  className="h-9 border-amber-400/35 bg-[#071a33]/40 px-4 text-xs font-semibold text-amber-300 transition-colors hover:border-amber-300/60 hover:bg-amber-400/10 hover:text-amber-200"
                >
                  Ver torneios
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <div aria-hidden="true" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="border-t border-amber-400/10" />
        </div>

        <section
          id="sobre"
          className="mx-auto max-w-7xl scroll-mt-20 px-4 py-14 sm:px-6 lg:px-8 lg:py-20"
        >
          <h2 className="text-center text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl">
            Sobre o GSX
          </h2>

          <div className="mt-10 grid gap-5 md:grid-cols-3 lg:gap-6">
            {PERFORMANCE_PILLARS.map((pillar) => (
              <TiltCard
                key={pillar.title}
                max={7}
                className="border border-amber-400/15 bg-[#0b294d] p-6 shadow-[0_18px_42px_rgba(0,0,0,0.28)] sm:p-7"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-amber-400/25 bg-amber-400/10">
                  <pillar.icon className="h-4 w-4 text-amber-400" />
                </div>
                <h3 className="mt-5 text-base font-semibold text-white">
                  {pillar.title}
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-slate-300">
                  {pillar.description}
                </p>
              </TiltCard>
            ))}
          </div>

          <div aria-hidden="true" className="mt-16 border-t border-amber-400/10" />

          <div className="mt-16 grid items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <p className="text-xs font-bold uppercase tracking-wider text-amber-400">
                Nossa História
              </p>
              <h2 className="mt-3 max-w-xl text-3xl font-bold leading-tight text-white sm:text-4xl">
                13 anos formando{" "}
                <span className="text-amber-400">campeões</span> de Sergipe para o
                Brasil.
              </h2>

              <div className="mt-6 max-w-xl space-y-4 text-base leading-relaxed text-slate-200">
                <p>
                  Desde 2013 o Grupo Santo de Xadrez é a equipe com mais rendimentos
                  em torneios escolares — hoje somos mais de{" "}
                  <span className="font-semibold text-amber-400">1000 alunos</span>{" "}
                  entre as escolas onde atuamos e os atletas do clube.
                </p>
                <p>
                  Representamos Sergipe nos{" "}
                  <span className="font-semibold text-amber-400">
                    Jogos Escolares Brasileiros
                  </span>{" "}
                  por 4 anos consecutivos (2023, 2024, 2025 e 2026), conquistando{" "}
                  <span className="font-semibold text-amber-400">
                    1 prata e 1 bronze
                  </span>
                  .
                </p>
                <p>
                  Trabalhamos com atletas{" "}
                  <span className="font-semibold text-amber-400">
                    a partir de 5 anos de idade
                  </span>
                  .
                </p>
              </div>
            </div>

            <div className="min-w-0 lg:col-span-6">
              <FeaturedAthletesCarousel />
            </div>
          </div>
        </section>

        <section
          aria-labelledby="upcoming-tournaments-heading"
          className="mx-auto max-w-7xl px-4 pb-12 pt-14 sm:px-6 lg:px-8"
        >
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2
                id="upcoming-tournaments-heading"
                className="text-3xl font-semibold text-white sm:text-4xl"
              >
                Próximos Torneios
              </h2>
              <p className="mt-1 text-sm text-slate-400">
                Calendário oficial curado pelo GSX.
              </p>
            </div>

            <Link
              to="/torneios"
              data-testid="upcoming-tournaments-all-link"
              className="text-sm font-medium text-amber-400 transition hover:text-amber-300"
            >
              Ver todos →
            </Link>
          </div>

          {tournaments.length > 0 ? (
            <div className="mt-7 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {tournaments.map((tournament) => (
                <TournamentPreviewCard
                  key={tournament.id}
                  tournament={tournament}
                  testId={`tournament-card-${tournament.id}`}
                />
              ))}
            </div>
          ) : (
            <p
              className="mt-7 rounded-xl border border-amber-400/20 bg-[#0b2342] p-6 text-sm text-slate-300"
              role="status"
            >
              {tournamentStatus === "loading"
                ? "Carregando torneios…"
                : tournamentStatus === "error"
                  ? "Não foi possível carregar os torneios agora."
                  : "Nenhum torneio futuro publicado no momento."}
            </p>
          )}
        </section>

        <div aria-hidden="true" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="border-t border-amber-400/10" />
        </div>

        <section className="mx-auto max-w-7xl px-4 pb-16 pt-10 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-amber-400/20 bg-[#0b2a50] p-6 sm:flex sm:items-center sm:justify-between sm:px-9 sm:py-8">
            <div>
              <h2 className="text-2xl font-semibold text-white">
                Comece sua jornada no GSX
              </h2>
              <p className="mt-2 text-sm text-slate-300">
                Aulas experimentais gratuitas e avaliação técnica personalizada.
              </p>
            </div>

            <Link
              to="/aula-experimental"
              className="mt-5 inline-block sm:mt-0"
            >
              <Button
                data-testid="cta-experimental-btn"
                className="bg-amber-500 px-5 font-bold text-[#071a33] hover:bg-amber-400"
              >
                Solicitar Aula Experimental
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
}
